import {
	ConsumptionRelationTypes,
	FuelTypes,
	FuelZoneTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { round } from '@shared/utils/math';

export const DUPLICATE_SPEED_GRADE_ERROR =
	'This speed already exists with the same fuel grade. Creating two speed profiles with different consumption is not allowed.';

export type FuelGradeSignature = {
	normal: Values<typeof FuelTypes> | null;
	eca: Values<typeof FuelTypes> | null;
};

type PerformanceEntryLike = {
	relation?: string | null;
	fuelZoneType?: string | null;
	fuelType?: string | null;
	condition?: string | null;
};

type SpeedLike = {
	ladenSpeed?: number | null;
	ballastSpeed?: number | null;
};

type ConsumptionSetLike = SpeedLike & {
	id?: number | null;
	PerformanceEntries?: PerformanceEntryLike[] | null;
	performanceEntries?: PerformanceEntryLike[] | null;
};

const getMainFuelType = (
	entries: PerformanceEntryLike[],
	fuelZoneType: Values<typeof FuelZoneTypes>,
): Values<typeof FuelTypes> | null => {
	const entry = entries.find((e) => (
		e.relation === ConsumptionRelationTypes.MAIN &&
		e.fuelZoneType === fuelZoneType &&
		e.fuelType != null
	));

	return (entry?.fuelType as Values<typeof FuelTypes> | undefined) ?? null;
};

export const getFuelGradeSignature = (
	performanceEntries: PerformanceEntryLike[] | null | undefined,
): FuelGradeSignature => {
	const entries = performanceEntries ?? [];

	return {
		normal: getMainFuelType(entries, FuelZoneTypes.NORMAL),
		eca: getMainFuelType(entries, FuelZoneTypes.ECA),
	};
};

export const hasSameSpeeds = (a: SpeedLike, b: SpeedLike): boolean => {
	if (
		a.ladenSpeed == null ||
		a.ballastSpeed == null ||
		b.ladenSpeed == null ||
		b.ballastSpeed == null
	) {
		return false;
	}

	return (
		round(a.ladenSpeed) === round(b.ladenSpeed) &&
		round(a.ballastSpeed) === round(b.ballastSpeed)
	);
};

export const hasSameFuelGradeSignature = (
	a: FuelGradeSignature,
	b: FuelGradeSignature,
): boolean => a.normal === b.normal && a.eca === b.eca;

const getEntries = (set: ConsumptionSetLike): PerformanceEntryLike[] => (
	set.PerformanceEntries ?? set.performanceEntries ?? []
);

export const isConflictingConsumptionSet = (
	a: ConsumptionSetLike,
	b: ConsumptionSetLike,
): boolean => {
	if (a.id != null && b.id != null && a.id === b.id) {
		return false;
	}

	if (!hasSameSpeeds(a, b)) {
		return false;
	}

	return hasSameFuelGradeSignature(
		getFuelGradeSignature(getEntries(a)),
		getFuelGradeSignature(getEntries(b)),
	);
};
